import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/use-toast';
import { gymsService } from '../../services/gyms';
import { ZoneManager } from '../../components/gym/ZoneManager';
import { HoldTypesPanel } from '../components/hold-types/HoldTypesPanel';
import { useGymAdmin } from '../context/gym-admin-context';
import { useCurrentGym } from '../hooks/useCurrentGym';

/**
 * Route-setting, for whichever kind of place is selected. A crag gets the
 * sectors and nothing else: suggested climb names are a gym's tool, for naming
 * a set of problems that go up together — a crag's routes are named by whoever
 * bolted them, years apart.
 */
export default function RouteSettingPage() {
  const { locationType, selectedGymSlug } = useGymAdmin();

  // Zones and nothing else, for the two kinds of place that have no permanent
  // list of names to draw on: a crag's routes are named by whoever bolted them,
  // and a stage's boulders are named for that weekend and then gone.
  if (locationType === 'crag' || locationType === 'competition') {
    if (!selectedGymSlug) return null;
    return (
      <div className="max-w-4xl space-y-6">
        <Card>
          <CardContent className="pt-6">
            <ZoneManager
              locationSlug={selectedGymSlug}
              isStaffMember
              locationType={locationType}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return <GymRouteSettingPage />;
}

/**
 * Route-setting section: manage zones/sectors and maintain the list of
 * suggested climb names used when creating climbs.
 */
function GymRouteSettingPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { gym, loading, setGym, slug } = useCurrentGym();

  const [suggestions, setSuggestions] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (gym) setSuggestions(gym.climb_name_suggestions || '');
  }, [gym]);

  const handleSave = async () => {
    if (!slug) return;
    setSaving(true);
    try {
      const updated = await gymsService.updateGym(slug, {
        climb_name_suggestions: suggestions || undefined,
      });
      setGym(updated);
      toast({ title: t('gymAdmin.saved') });
    } catch (error) {
      toast({
        title: t('gymAdmin.saveError'),
        description: error instanceof Error ? error.message : t('errors.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Skeleton className="h-96 w-full max-w-4xl" />;
  if (!gym || !slug) return null;

  return (
    <div className="max-w-4xl space-y-6">
      {/* Zones / sectors */}
      <Card>
        <CardContent className="pt-6">
          <ZoneManager locationSlug={slug} isStaffMember={true} />
        </CardContent>
      </Card>

      {/* What the gym's holds look like — the list every climb picks from. */}
      <Card>
        <CardContent className="pt-6">
          <HoldTypesPanel locationId={slug} />
        </CardContent>
      </Card>

      {/* Suggested climb names */}
      <Card>
        <CardHeader>
          <CardTitle>{t('gymSettings.climbNameSuggestions')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="climbNameSuggestions">{t('gymSettings.climbNameSuggestions')}</Label>
            <p className="text-xs text-muted-foreground">{t('gymSettings.climbNameSuggestionsHelper')}</p>
            <Textarea
              id="climbNameSuggestions"
              rows={6}
              placeholder={t('gymSettings.climbNameSuggestionsPlaceholder')}
              value={suggestions}
              onChange={(e) => setSuggestions(e.target.value)}
            />
          </div>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? t('gymAdmin.saving') : t('gymAdmin.save')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
